import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound, Plus, RefreshCw, Server, TriangleAlert, Unplug } from 'lucide-react'
import type { SiteDTO, SshKeyDTO } from '@shared/types'
import { Role } from '@shared/types'
import { api, errorMessage } from '@/lib/api'
import { formatNumber, pluralize } from '@/lib/format'
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  EmptyState,
  Modal,
  Skeleton,
  useToast
} from '@/components/ui'
import PageHeader from '@/components/layout/PageHeader'
import { useAuthStore } from '@/stores/useAuthStore'
import { useSitesStore } from '@/stores/useSitesStore'
import ConnectSiteDialog from './ConnectSiteDialog'
import SiteCard from './SiteCard'

const SKELETON_IDS = ['s1', 's2', 's3', 's4', 's5', 's6']
const STAT_IDS = ['a', 'b', 'c', 'd']

function SiteCardSkeleton(): React.ReactElement {
  return (
    <Card className="@container flex flex-col">
      <CardBody className="flex-1 space-y-4">
        <div className="flex items-start gap-3">
          <Skeleton className="size-10 shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 @sm:grid-cols-4">
          {STAT_IDS.map((id) => (
            <Skeleton key={id} className="h-14" />
          ))}
        </div>
      </CardBody>
      <CardFooter>
        <Skeleton className="h-8 w-full" />
      </CardFooter>
    </Card>
  )
}

/**
 * The sites connected in THIS session — not a history.
 *
 * Nothing on this page outlives the session: signing out, the absolute expiry and
 * the idle sweep all delete it. The copy here has to say so at every step, because
 * a page that looks like a saved list is how somebody loses an hour of review.
 */
export default function SitesPage(): React.ReactElement {
  const navigate = useNavigate()
  const { toast } = useToast()

  /** Only an admin can configure the instance WP Engine key, or reach /keys at all. */
  const isAdmin = useAuthStore((state) => state.user?.role) === Role.Admin

  const sites = useSitesStore((state) => state.sites)
  const loading = useSitesStore((state) => state.loading)
  const error = useSitesStore((state) => state.error)
  const load = useSitesStore((state) => state.load)
  const remove = useSitesStore((state) => state.remove)

  const [sshKeys, setSshKeys] = useState<SshKeyDTO[]>([])
  const [keysLoading, setKeysLoading] = useState(true)
  const [connectOpen, setConnectOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SiteDTO | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    void load()
  }, [load])

  // Keys are read here rather than in the dialog: the empty state has to know
  // whether a key exists before it can offer the right next step.
  useEffect(() => {
    let live = true

    const loadKeys = async (): Promise<void> => {
      try {
        const result = await api.get<SshKeyDTO[]>('/ssh-keys')
        if (live) setSshKeys(result)
      } catch {
        // Only the connect flow depends on this, and it reports its own failure.
      } finally {
        if (live) setKeysLoading(false)
      }
    }

    void loadKeys()
    return () => {
      live = false
    }
  }, [])

  const confirmDelete = useCallback(async (): Promise<void> => {
    const target = deleteTarget
    if (!target) return

    setDeleting(true)
    try {
      await remove(target.id)
      setDeleteTarget(null)
      toast({
        title: 'Site disconnected',
        description: `${target.installName} and everything Argus fetched for it are gone.`,
        variant: 'success'
      })
    } catch (cause) {
      toast({
        title: 'Could not disconnect that site',
        description: errorMessage(cause),
        variant: 'error'
      })
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, remove, toast])

  const hasSites = sites.length > 0
  const busy = (loading || keysLoading) && !hasSites
  const hasKeys = sshKeys.length > 0
  const unsavedApproved =
    deleteTarget === null ? 0 : Math.max(0, deleteTarget.approvedCount - deleteTarget.savedCount)

  return (
    <>
      <PageHeader
        title="Sites"
        description="The WP Engine installs connected in this session. Each one is a temporary working surface: open it, generate and approve alt text, then save that text back to WordPress — signing out or letting the session expire removes the site and everything fetched with it."
        actions={
          <>
            <Button
              variant="secondary"
              iconLeft={<RefreshCw />}
              loading={loading && hasSites}
              onClick={() => void load()}
            >
              Refresh
            </Button>
            <Button iconLeft={<Plus />} onClick={() => setConnectOpen(true)}>
              Connect site
            </Button>
          </>
        }
      />

      {error && hasSites ? (
        <div
          role="alert"
          className="mb-4 flex items-start gap-2.5 rounded-card border border-bad-500/40 bg-bad-100/70 px-4 py-3 text-sm text-bad-700 dark:bg-bad-950/60 dark:text-bad-300"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}

      {busy ? (
        <div aria-busy="true" className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <span className="sr-only">Loading the sites connected in this session…</span>
          {SKELETON_IDS.map((id) => (
            <SiteCardSkeleton key={id} />
          ))}
        </div>
      ) : hasSites ? (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sites.map((site) => (
            <li key={site.id} className="flex">
              <SiteCard site={site} sshKeys={sshKeys} onDelete={setDeleteTarget} />
            </li>
          ))}
        </ul>
      ) : error ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert />}
            title="Could not load your sites"
            description={error}
            action={
              <Button variant="secondary" iconLeft={<RefreshCw />} onClick={() => void load()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : hasKeys ? (
        <Card>
          <EmptyState
            icon={<Server />}
            title="Nothing connected in this session"
            description="Connect a WP Engine install and Argus reads its media library over SSH, so you can generate, review and save alt text in one sitting. The connection lasts only as long as this session — what you write back to WordPress is what keeps."
            action={
              <Button iconLeft={<Plus />} onClick={() => setConnectOpen(true)}>
                Connect a site
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <EmptyState
            icon={<KeyRound />}
            title="WP Engine access isn't set up yet"
            description={
              isAdmin
                ? 'Argus talks to WP Engine over SSH, and no key has been configured for this instance yet. Set one up once and every user can connect sites with it — nobody else needs a key of their own.'
                : 'Argus talks to WP Engine over SSH, and an administrator needs to configure the key first. It is a one-time setup for the whole team — once it is done you can connect sites without any key of your own.'
            }
            action={
              // Non-admins have no route to /keys and nothing to do there, so they get
              // the explanation without a button that leads nowhere.
              isAdmin ? (
                <Button iconLeft={<KeyRound />} onClick={() => navigate('/keys')}>
                  Set up the WP Engine key
                </Button>
              ) : undefined
            }
          />
        </Card>
      )}

      <ConnectSiteDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        sshKeys={sshKeys}
        keysLoading={keysLoading}
      />

      <Modal
        open={deleteTarget !== null}
        onClose={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        title="Disconnect site"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={deleting}
              onClick={() => setDeleteTarget(null)}
            >
              Keep it connected
            </Button>
            <Button
              variant="danger"
              iconLeft={<Unplug />}
              loading={deleting}
              onClick={() => void confirmDelete()}
            >
              Disconnect site
            </Button>
          </>
        }
      >
        {deleteTarget ? (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-fg">
              Disconnect <span className="font-semibold">{deleteTarget.installName}</span>?
            </p>
            <div className="rounded-control border border-warn-500/40 bg-warn-100/70 px-3 py-2.5 text-sm leading-relaxed text-warn-700 dark:bg-warn-950/60 dark:text-warn-300">
              {formatNumber(deleteTarget.imageCount)}{' '}
              {pluralize(deleteTarget.imageCount, 'fetched image')} go with it, along with every
              generated or edited alt text and its approval state.
              {unsavedApproved > 0
                ? ` ${formatNumber(unsavedApproved)} approved ${pluralize(unsavedApproved, 'image')} has not been written to WordPress yet — save first or that text is lost.`
                : ' Argus keeps no copy to come back to.'}
            </div>
            <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-fg-muted">
              <li>
                Alt text already saved to WordPress stays on the live site — only Argus&rsquo;s
                working copy goes away.
              </li>
              <li>
                Your SSH key is untouched, so you can connect this install again whenever you like.
                It comes back empty: there is no earlier pass to pick up from.
              </li>
            </ul>
          </div>
        ) : null}
      </Modal>
    </>
  )
}

export { SitesPage }
